/**
 * Utility functions for date interval selection in the availability calendar
 */

const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * True iff `value` is a real calendar date in strict `YYYY-MM-DD` form.
 *
 * The shape regex alone is not enough: `2027-13-45` (month 13, day 45) passes
 * a `/^\d{4}-\d{2}-\d{2}$/` test but is not a date. We additionally parse it
 * (UTC, to avoid timezone drift) and round-trip it back to a string — an
 * impossible date either fails to parse (`NaN`) or normalises to a different
 * string, so it's rejected. Pure; the checkout API routes gate on this before
 * touching PYN (docs/82 · A7), so garbage like `2027-13-45` can't reach the
 * upstream booking engine.
 */
export function isValidIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
}

/**
 * Reduce any date-ish string to its `YYYY-MM-DD` date part. Accepts plain
 * dates (`2027-01-01`, returned unchanged) and datetimes
 * (`2027-01-01T00:00:00Z` → `2027-01-01`). Used to compare dates from external
 * systems (PYN PMS) defensively: if PYN ever serialises a datetime instead of a
 * plain date, a raw `===` would silently treat every booking as "dates
 * changed" and disable the double-book overlay guard (docs/82 · A7). Normalise
 * both sides first so the comparison survives a serialisation change.
 */
export function normalizeToIsoDate(value: string): string {
  return value.length >= 10 ? value.slice(0, 10) : value;
}

/**
 * Compare two ISO date strings (YYYY-MM-DD format)
 * @returns negative if a < b, 0 if equal, positive if a > b
 */
export function compareDates(a: string, b: string): number {
  return a.localeCompare(b);
}

/**
 * Check if a date is in range (checkIn inclusive, checkOut exclusive)
 * This matches hotel booking logic where checkout day is not a "night"
 */
export function isDateInRange(date: string, checkIn: string, checkOut: string): boolean {
  return compareDates(date, checkIn) >= 0 && compareDates(date, checkOut) < 0;
}

/**
 * Check if a date is strictly between checkIn and checkOut (both exclusive)
 * Used for highlighting the "middle" days of a selection
 */
export function isDateBetween(date: string, checkIn: string, checkOut: string): boolean {
  return compareDates(date, checkIn) > 0 && compareDates(date, checkOut) < 0;
}

/**
 * Format date as YYYY-MM-DD in local timezone (not UTC)
 */
function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Add one day to an ISO date string
 */
export function addOneDay(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00'); // Use noon to avoid DST issues
  date.setDate(date.getDate() + 1);
  return formatDateLocal(date);
}

/**
 * Add `n` days to an ISO date string (n ≥ 1). Used by the min-stay first-click
 * auto-bump (doc 81 §12): clicking a check-in with `minStay = N` sets check-out
 * to `addDays(checkIn, N)`. Noon anchor avoids DST edge shifts.
 */
export function addDays(dateStr: string, n: number): string {
  const date = new Date(dateStr + 'T12:00:00');
  date.setDate(date.getDate() + n);
  return formatDateLocal(date);
}

/**
 * Get all dates between start and end (start inclusive, end exclusive)
 */
export function getDatesBetween(start: string, end: string): string[] {
  const dates: string[] = [];
  let current = start;
  while (compareDates(current, end) < 0) {
    dates.push(current);
    current = addOneDay(current);
  }
  return dates;
}

/**
 * Calculate number of nights between check-in and check-out
 */
export function calculateNights(checkIn: string, checkOut: string): number {
  const startDate = new Date(checkIn + 'T12:00:00');
  const endDate = new Date(checkOut + 'T12:00:00');
  const diffTime = endDate.getTime() - startDate.getTime();
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Largest aggregate `minStay` on any night of a stay (doc 81 §14). PYN applies
 * a night's minimum to EVERY stay that includes that night — not only to
 * arrivals on it — so a min-3 Sunday also binds a Sat→Mon stay. `minStayOf`
 * returns a day's `CalendarDay.minStay` (undefined for a day not loaded ⇒ 1).
 *
 * The per-day aggregate is the min across that day's open plans, so this is a
 * LOWER bound of the per-plan requirement — permissive by design for the UX
 * tier; checkout's per-plan `planMeetsMinStay` stays authoritative (§12.3
 * two-tier model). `stayNights` must be check-out exclusive.
 */
export function requiredMinStay(
  stayNights: string[],
  minStayOf: (date: string) => number | undefined
): number {
  return stayNights.reduce((max, date) => {
    const n = minStayOf(date);
    return typeof n === 'number' && Number.isFinite(n) && n > max ? n : max;
  }, 1);
}

/** Longest stay the min-stay snap will extend to — PYN data is untrusted. */
const MAX_MIN_STAY_SNAP_NIGHTS = 31;

/**
 * Earliest check-out at or after `checkOut` whose stay meets the minimum of
 * every night it covers (doc 81 §14). Pure; shared by all four date pickers.
 *
 * Extending a stay can pull in a night with a longer minimum (Sat→Mon covers a
 * min-3 Sunday ⇒ Sat→Tue), so this iterates to the fixed point: lengthen to
 * the current requirement, re-check the lengthened range, repeat. The night
 * count only ever grows, and growth is capped at 31 nights (or the guest's own
 * longer pick) so garbage PYN data can't spin.
 *
 * Note the valid check-outs from an arrival need not be contiguous: from a
 * Saturday with a min-3 Sunday, Sat→Sun (1 night, Sunday not covered) is valid,
 * Sat→Mon is not, Sat→Tue is. Snapping a click UP is the established picker
 * behaviour (doc 81 §12.3), so a Monday click lands on Tuesday.
 */
export function snapCheckOutToMinStay(
  checkIn: string,
  checkOut: string,
  minStayOf: (date: string) => number | undefined
): string {
  const chosen = Math.max(1, calculateNights(checkIn, checkOut));
  // The cap only limits GROWTH — a guest's own longer pick is never shortened.
  const cap = Math.max(chosen, MAX_MIN_STAY_SNAP_NIGHTS);
  let nights = chosen;
  while (nights < cap) {
    const required = requiredMinStay(getDatesBetween(checkIn, addDays(checkIn, nights)), minStayOf);
    if (nights >= required) break;
    nights = Math.min(required, cap);
  }
  return addDays(checkIn, nights);
}

export interface IntervalPrice {
  nights: number;
  /** Per-night prices that were actually found in `calendarDays`. */
  nightlyPrices: number[];
  /** Sum of `nightlyPrices` — only a TRUE total when `hasAllPrices`. */
  totalPrice: number;
  /** True iff every night in the range had a known price. */
  hasAllPrices: boolean;
}

/**
 * Sum the nightly prices for a stay and report whether EVERY night had a
 * known price. Pure; shared by the room card and the room-detail booking
 * summary (docs/82 · C6/C7). A stay that crosses into a month the client
 * hasn't loaded yet yields `hasAllPrices === false` with a PARTIAL
 * `totalPrice` — callers MUST gate any displayed total on `hasAllPrices` and
 * withhold the figure when it is false, otherwise the guest sees a too-low
 * total next to "Book Now".
 */
export function computeIntervalPrice(
  checkIn: string,
  checkOut: string,
  calendarDays: { date: string; price: number | null }[],
): IntervalPrice {
  const nights = calculateNights(checkIn, checkOut);
  const datesInRange = getDatesBetween(checkIn, checkOut);
  const nightlyPrices: number[] = [];
  for (const date of datesInRange) {
    const dayData = calendarDays.find((d) => d.date === date);
    if (dayData?.price !== null && dayData?.price !== undefined) {
      nightlyPrices.push(dayData.price);
    }
  }
  const totalPrice = nightlyPrices.reduce((sum, p) => sum + p, 0);
  return {
    nights,
    nightlyPrices,
    totalPrice,
    hasAllPrices: nightlyPrices.length === nights,
  };
}

/**
 * Format date as "Dec 15" style
 */
export function formatShortDate(dateStr: string): string {
  const [, month, day] = dateStr.split('-').map(Number);
  return `${MONTH_NAMES_SHORT[month - 1]} ${day}`;
}

/**
 * Get month key from date string for caching (e.g., "2025-12")
 */
export function getMonthKey(dateStr: string): string {
  return dateStr.substring(0, 7); // "YYYY-MM"
}

/**
 * Get month key from year and month numbers
 */
export function getMonthKeyFromParts(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Parse a `?month=YYYY-MM` deep link — an offer page opening the rooms list
 * (and a room card the room detail page) on a month without selecting dates.
 * Returns null for anything malformed or for a month before `now`'s, so the
 * caller falls back to its usual current-month default. Dates, when present,
 * always win over this — callers only consult it without a valid range.
 */
export function parseMonthParam(
  raw: unknown,
  now: Date = new Date(),
): { year: number; month: number } | null {
  if (typeof raw !== 'string') return null;
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(raw);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const isPast = year * 12 + month < now.getFullYear() * 12 + (now.getMonth() + 1);
  return isPast ? null : { year, month };
}

/**
 * Query string a rooms-list card appends to its room-detail link: the filter
 * dates when both are set, else the month being browsed as `?month=` — so the
 * room page's calendar opens where the guest was looking (an offer's January
 * deep link would otherwise snap back to today's month one click later).
 * Omitted for the current month, which is the detail page's default anyway.
 */
export function roomDetailQuery(
  checkIn: string | null | undefined,
  checkOut: string | null | undefined,
  calendarYear: number,
  calendarMonth: number,
  now: Date = new Date(),
): string {
  if (checkIn && checkOut) return `?checkIn=${checkIn}&checkOut=${checkOut}`;
  const isCurrentMonth = calendarYear === now.getFullYear() && calendarMonth === now.getMonth() + 1;
  return isCurrentMonth ? '' : `?month=${getMonthKeyFromParts(calendarYear, calendarMonth)}`;
}

/**
 * Key for the rooms list's client-side bulk-calendar month cache.
 *
 * ⚠️ The month is deliberately NOT zero-padded here — `"2027-1"`, not
 * `"2027-01"`. That is the format the cache has always been written with, and
 * it differs from `getMonthKey`/`getMonthKeyFromParts` above (padded, used for
 * display and for the per-room calendars' own caches). Mixing the two silently
 * misses every month from January to September, so every writer AND reader of
 * that cache goes through this function.
 */
export function monthCacheKey(year: number, month: number): string {
  return `${year}-${month}`;
}

/** {@link monthCacheKey} for the month an ISO `YYYY-MM-DD` date falls in. */
export function monthCacheKeyForDate(isoDate: string): string {
  return monthCacheKey(
    parseInt(isoDate.substring(0, 4), 10),
    parseInt(isoDate.substring(5, 7), 10),
  );
}

/**
 * Pick, per room slug, the calendar days covering every NIGHT of
 * `[checkIn, checkOut)` out of a month cache that may hold several months.
 *
 * Exists because a stay can cross a month boundary — the New Year's package
 * runs Dec 30 → Jan 3 — while each room card's own calendar holds only the one
 * month it is rendering. Summing a stay from that alone yields
 * `hasAllPrices: false` and the C6/C7 guards then withhold the total, leaving a
 * festive stay with no price at all. The missing month is already in the cache
 * (the rooms list fetches every month the filter range touches in order to test
 * availability); this hands it to the cards.
 *
 * Nights only: `getDatesBetween` is check-out exclusive, so the departure day —
 * which is not a priced night — can never enter the sum.
 *
 * Returns `{}` for an absent or empty range. A slug is absent from the result
 * when the cache holds nothing for it; a slug present but short of some nights
 * is reported as-is, leaving the `hasAllPrices` gate to the caller.
 */
export function collectRangeDaysBySlug<T extends { date: string }>(
  monthCache: ReadonlyMap<string, Record<string, { days: T[] }>>,
  checkIn: string | null | undefined,
  checkOut: string | null | undefined,
): Record<string, T[]> {
  if (!checkIn || !checkOut) return {};
  const nights = getDatesBetween(checkIn, checkOut);
  if (nights.length === 0) return {};

  const wanted = new Set(nights);
  const monthKeys = new Set(nights.map(monthCacheKeyForDate));

  const result: Record<string, T[]> = {};
  for (const key of monthKeys) {
    const monthData = monthCache.get(key);
    if (!monthData) continue;
    for (const [slug, roomData] of Object.entries(monthData)) {
      for (const day of roomData.days) {
        if (!wanted.has(day.date)) continue;
        (result[slug] ||= []).push(day);
      }
    }
  }
  return result;
}

/**
 * Months a calendar must have loaded before it can judge a minimum-stay
 * auto-range.
 *
 * Clicking a check-in auto-fills check-out to `date + minStay`. That range is
 * then screened for blocked nights, and a night with no loaded data counts as
 * blocked — "cannot verify" fails safe (doc 82 · A7). When the window reaches
 * into a month the browser has not downloaded, that safety rule fires on
 * *unknown* rather than *unavailable* nights and the pick silently collapses to
 * one night — under a banner promising a longer minimum, and unbookable at
 * checkout, which drops any plan below its own `MinStays`. This reports the
 * months to fetch so the screen runs on real data instead.
 *
 * **Minimum stay is not a festive concept.** It is a plain per-plan, per-night
 * PYN restriction (`MinStays`, doc 81 §14), and in the live config it sits mostly on the
 * evergreen plans — two-night weekends, a three-night late-November window —
 * with the holiday packages merely being the longest cases. Nothing here may
 * key off `isFestive`; a day carrying `minStay > 1` is the whole condition.
 *
 * Pass only the days the guest can actually click — the month on screen — as
 * `daysToCheck`, and everything already downloaded as `knownDates`. Scanning
 * the whole cache instead would chain: each preloaded month's own tail would
 * pull in the next, walking forward without bound. A month nobody is looking at
 * has no window worth judging yet.
 *
 * Returns the missing months chronologically, deduplicated, `[]` when the
 * loaded data already covers every window (the common case — a month with no
 * min-stay near its end costs no fetch at all).
 */
export function monthsMissingForMinStay<
  T extends { date: string; isPast: boolean; minStay: number },
>(daysToCheck: Iterable<T>, knownDates?: Iterable<string>): { year: number; month: number }[] {
  const days: T[] = [];
  const loaded = new Set<string>();
  for (const day of daysToCheck) {
    days.push(day);
    loaded.add(day.date);
  }
  if (knownDates) for (const date of knownDates) loaded.add(date);

  const missing = new Map<string, { year: number; month: number }>();
  for (const day of days) {
    // Past days can't be clicked, so their windows are never evaluated.
    if (day.isPast || !(day.minStay > 1)) continue;
    // The window is `minStay` nights from the arrival day, check-out exclusive
    // — exactly the range the blocked-night screen walks. Capped because
    // `minStay` comes from PYN and a garbage value must not spin this loop.
    const nights = Math.min(day.minStay, 31);
    let night = day.date;
    for (let i = 0; i < nights; i++) {
      if (!loaded.has(night)) {
        const year = parseInt(night.substring(0, 4), 10);
        const month = parseInt(night.substring(5, 7), 10);
        missing.set(`${year}-${String(month).padStart(2, '0')}`, { year, month });
      }
      night = addOneDay(night);
    }
  }

  return [...missing.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, m]) => m);
}
